import React, { useEffect, useState } from "react";
import { TableRow, useTheme, Typography } from "@mui/material";
import { styled } from "@mui/system";
import {
  Tablecell,
  calculateAndFormat,
  formatValue,
  calculateAndFormatNoAbs,
} from "../../component/Methods";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { useSelector } from "react-redux";
import { selectStrategies, selectFunds } from "../../Redux/Reducers/selectors";

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#5A5A5A",
    color: "#fff",
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
  },
}));

const PositionTable = React.memo((props) => {
  const { row, fundVal, strategyVal, visible, positionType = "long" } = props;
  const isLong = positionType === "long";
  const funds = useSelector(selectFunds);
  const strategies = useSelector(selectStrategies);
  const [myFund, setMyFund] = useState({});
  const [myStrategy, setMyStrategy] = useState({});
  const [formattedSize, setFormattedSize] = useState("");
  const [
    formattedDeltaAdjustedShareCount,
    setFormattedDeltaAdjustedShareCount,
  ] = useState("");
  const [formattedLast, setFormattedLast] = useState("");
  const [ticker, setTicker] = useState("");
  const [formattedCurrent, setFormattedCurrent] = useState("");
  const [formattedCh, setFormattedCh] = useState("");
  const [formattedPL, setFormattedPL] = useState("");
  const [formattedChPercentage, setFormattedChPercentage] = useState("");
  const [formattedCost, setFormattedCost] = useState(0);
  const [formattedAvgCost, setformattedAvgCost] = useState(0);
  const [formattedPerAvgCost, setformattedPerAvgCost] = useState(0);
  const [tdg, setTdg] = useState(0);

  useEffect(() => {
    if (isLong) {
      // Long position data
      setFormattedSize(formatValue(row?.long_size));
      setFormattedDeltaAdjustedShareCount(
        calculateAndFormat(row?.long_deltaadjustedsharecount)
      );
      setTicker(row?.long_tickerSymbol);
      setFormattedLast(formatValue(row?.long_last));
      setFormattedCurrent(formatValue(row.long_current));
      setFormattedCh(row?.long_ch);
      setFormattedPL(row?.long_pL);
      setFormattedCost(row?.long_cost);
      setFormattedChPercentage(row?.long_chpercentage);
      setformattedAvgCost(row?.long_pl_avg_cost);
      setformattedPerAvgCost(row?.long_percentage_avg_cost);
      setTdg(row?.long_tdg);
    } else {
      // Short position data
      setFormattedSize(formatValue(Math.abs(row.short_size)));
      setFormattedDeltaAdjustedShareCount(
        calculateAndFormat(row.short_deltaadjustedsharecount * -1)
      );
      setTicker(row?.short_tickerSymbol);
      setFormattedLast(formatValue(row.short_last));
      setFormattedCurrent(formatValue(row.short_current));
      setFormattedCh(row.short_ch);
      setFormattedPL(row.short_pL);
      setFormattedCost(row?.short_cost);
      setFormattedChPercentage(row.short_chpercentage);
      setformattedAvgCost(row?.short_pl_avg_cost);
      setformattedPerAvgCost(row?.short_percentage_avg_cost);
      setTdg(row?.short_tdg);
    }
  }, [row, isLong]);

  const handlePosition = (obj) => {
    if (
      fundVal &&
      strategyVal &&
      fundVal.toUpperCase() !== "ALL" &&
      strategyVal.toUpperCase() !== "ALL"
    ) {
      const fund = funds?.find((i) => i.abbreviation == fundVal);
      const strategy = strategies?.find((i) => i.name == strategyVal);
      setMyFund(fund);
      setMyStrategy(strategy);
    } else {
      setMyFund({});
      setMyStrategy({});
    }
  };

  const theme = useTheme();

  // Helper functions for color logic (inverted for short)
  const getChColor = (value) => {


    if (!value) return theme.palette.text.primary;
    const isPositive = value >= 0;
    const shouldBeGreen = isLong ? isPositive : !isPositive;
    return shouldBeGreen
      ? theme.palette.mode === "dark"
        ? "#4ade80"
        : "#16a34a"
      : theme.palette.mode === "dark"
      ? "#f87171"
      : "#dc2626";
  };


  const getTdgColor = (value) => {
    if (value === 0) return theme.palette.text.primary;
    const isPositive = value >= 0;
    const shouldBeGreen = isLong ? isPositive : !isPositive;
    return shouldBeGreen
      ? theme.palette.mode === "dark"
        ? "#4ade80"
        : "#16a34a"
      : theme.palette.mode === "dark"
      ? "#f87171"
      : "#dc2626";
  };

  const formatSize = () => {
    if (!formattedSize) return "0.00%";
    if (isLong) {
      return `${formattedSize}%`;
    } else {
      return `(${parseFloat(formattedSize).toFixed(2)}%)`;
    }
  };

  const formatDeltaAdjustedShareCount = () => {
    if (!formattedDeltaAdjustedShareCount) return "";
    if (isLong) {
      return formattedDeltaAdjustedShareCount;
    } else {
      return `(${formattedDeltaAdjustedShareCount})`;
    }
  };

  const formatCh = () => {
    if (!formattedCh) return "";
    if (isLong) {
      return formattedCh >= 0
        ? parseFloat(formattedCh).toFixed(2)
        : `(${(formattedCh * -1).toFixed(2)})`;
    } else {
      return formattedCh >= 0
        ? `${formattedCh?.toFixed(2)}`
        : `(${parseFloat(Math.abs(formattedCh)).toFixed(2)})`;
    }
  };

  const formatPL = () => {
    if (!formattedPL) return "";
    if (isLong) {
      return formattedPL >= 0
        ? Math.round(formattedPL).toLocaleString()
        : `(${(Math.round(formattedPL) * -1).toLocaleString()})`;
    } else {
      return formattedPL >= 0
        ? `${Math.round(formattedPL).toLocaleString()}`
        : `(${(Math.round(formattedPL) * -1).toLocaleString()})`;
    }
  };

  const formatChPercentage = () => {
    if (!formattedChPercentage) return "";
    if (isLong) {
      return formattedChPercentage >= 0
        ? `${formattedChPercentage?.toFixed(3)}%`
        : `(${Math.abs(formattedChPercentage).toFixed(3)}%)`;
    } else {
      return formattedChPercentage >= 0
        ? `${formattedChPercentage?.toFixed(3)}%`
        : `(${(formattedChPercentage * -1).toFixed(3)}%)`;
    }
  };

  return (
    <React.Fragment>
      <TableRow
        sx={{
          backgroundColor: theme.palette.grey[100],
          "&:hover": {
            background: theme.palette.grey[200],
            cursor: "pointer",
          },
        }}
        onClick={() => {
          handlePosition(row);
        }}
      >
        <Tablecell
          style={{ width: "50px" }}
          align="right"
          sx={{
            paddingRight: isLong ? "5px" : "2px",
            color: theme.palette.text.primary,
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? theme.palette.grey[300]
                : theme.palette.grey[300]
            }`,
          }}
        >
          {formatSize()}
        </Tablecell>
        <Tablecell
          align="right"
          sx={{
            paddingRight: "5px",
            color: theme.palette.text.primary,
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? theme.palette.grey[300]
                : theme.palette.grey[300]
            }`,
          }}
        >
          {formatDeltaAdjustedShareCount()}
        </Tablecell>

        <Tablecell
          style={{
            width: "20px",
            paddingLeft: "5px",
            cursor: "pointer",
            color: theme.palette.text.primary,
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? theme.palette.grey[300]
                : theme.palette.grey[300]
            }`,
          }}
          align="left"
        >
          {ticker ? ticker.toUpperCase() : ""}
        </Tablecell>
        <HtmlTooltip
          title={
            <React.Fragment>
              <Typography color="inherit" sx={{ fontSize: "12px" }}>
                Market:<b> {row?.dataSource} </b>
              </Typography>

              <Typography color="inherit" sx={{ fontSize: "12px" }}>
                Updated: <b>{row?.fetched}</b>
              </Typography>
            </React.Fragment>
          }
        >
          <Tablecell
            style={{
              width: "20px",
              paddingRight: "5px",
              backgroundColor: row?.valid ? "#baffc8" : "#ffdcdc",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? theme.palette.grey[300]
                  : theme.palette.grey[300]
              }`,
            }}
            align="right"
          >
            {formattedLast || ""}
          </Tablecell>
        </HtmlTooltip>
        <HtmlTooltip
          title={
            <React.Fragment>
              <Typography color="inherit" sx={{ fontSize: "12px" }}>
                Market:<b> {row?.dataSource} </b>
              </Typography>

              <Typography color="inherit" sx={{ fontSize: "12px" }}>
                Updated: <b>{row?.fetched}</b>
              </Typography>
            </React.Fragment>
          }
        >
          <Tablecell
            style={{
              width: "20px",
              paddingRight: "5px",
              backgroundColor: row?.valid ? "#baffc8" : "#ffdcdc",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? theme.palette.grey[300]
                  : theme.palette.grey[300]
              }`,
            }}
            align="right"
          >
            {formattedCurrent || ""}
          </Tablecell>
        </HtmlTooltip>
        {formattedCh ? (
          <Tablecell
            align="right"
            style={{
              color: getChColor(formattedCh),
              paddingRight: "2px",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? theme.palette.grey[300]
                  : theme.palette.grey[300]
              }`,
            }}
          >
            {formatCh()}
            {isLong && " "}
          </Tablecell>
        ) : (
          <Tablecell align="right"></Tablecell>
        )}

        {formattedPL ? (
          <Tablecell
            align="right"
            style={{
              color: isLong
                ? formattedPL >= 0
                  ? theme.palette.mode === "dark"
                    ? "#4ade80"
                    : "#16a34a"
                  : theme.palette.mode === "dark"
                  ? "#f87171"
                  : "#dc2626"
                : formattedPL >= 0
                ? theme.palette.mode === "dark"
                  ? "#4ade80"
                  : "#16a34a"
                : theme.palette.mode === "dark"
                ? "#f87171"
                : "#dc2626",
              paddingRight: "2px",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? theme.palette.grey[300]
                  : theme.palette.grey[300]
              }`,
            }}
          >
            {formatPL()}
            {isLong && " "}
          </Tablecell>
        ) : (
          <Tablecell
            align="right"
            style={{
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? theme.palette.grey[300]
                  : theme.palette.grey[300]
              }`,
            }}
          ></Tablecell>
        )}
        {formattedChPercentage ? (
          <Tablecell
            style={{
              color: formattedChPercentage >= 0 
              ? (theme.palette.mode === 'dark' ? "#4ade80" : "#16a34a") 
              : (theme.palette.mode === 'dark' ? "#f87171" : "#dc2626"),
              width: "20px",
              paddingRight: "2px",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? theme.palette.grey[300]
                  : theme.palette.grey[300]
              }`,
            }}
            align="right"
          >
            {formatChPercentage()}
          </Tablecell>
        ) : (
          <Tablecell
            align="right"
            style={{
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? theme.palette.grey[300]
                  : theme.palette.grey[300]
              }`,
            }}
          ></Tablecell>
        )}
        {visible ? (
          <>
            <Tablecell
              align="right"
              style={{
                paddingRight: "5px",
                color: theme.palette.text.primary,
                border: `1px solid ${
                  theme.palette.mode === "dark"
                    ? theme.palette.grey[300]
                    : theme.palette.grey[300]
                }`,
              }}
            >
              {formattedAvgCost >= 0
                ? calculateAndFormatNoAbs(formattedAvgCost)
                : `(${calculateAndFormatNoAbs(Math.abs(formattedAvgCost))})`}
            </Tablecell>
            <Tablecell
              align="right"
              style={{
                paddingRight: "5px",
                color: theme.palette.text.primary,
                border: `1px solid ${
                  theme.palette.mode === "dark"
                    ? theme.palette.grey[300]
                    : theme.palette.grey[300]
                }`,
              }}
            >
              {formattedPerAvgCost >= 0
                ? `${formattedPerAvgCost?.toFixed(2)}%`
                : `(${Math.abs(formattedPerAvgCost).toFixed(2)}%)`} 
            </Tablecell>
          </>
        ) : null}

        <Tablecell
          align="right"
          style={{
            paddingRight: "5px",
            color: theme.palette.text.primary,
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? theme.palette.grey[300]
                : theme.palette.grey[300]
            }`,
          }}
        >
          {isLong ? "" : " "}
          {formattedCost?.toFixed(2)}
        </Tablecell>
        <Tablecell
          align="right"
          style={{
            color: getTdgColor(tdg),
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? theme.palette.grey[300]
                : theme.palette.grey[300]
            }`,
            width: "20px",
            paddingRight: "5px",
          }}
        >
          {tdg == 0
            ? ""
            : tdg >= 0
            ? Math.round(tdg).toLocaleString() + " "
            : `(${(Math.round(tdg) * -1).toLocaleString()})`}
        </Tablecell>
      </TableRow>
    </React.Fragment>
  );
});

// Memoize the component to prevent unnecessary re-renders
export default React.memo(PositionTable);
